import React, { useRef } from "react";
import { Animated, TouchableOpacity, ViewStyle, StyleProp, AccessibilityRole } from "react-native";

import { useAppTheme } from "../design-system/ThemeProvider";

interface GlowTouchableProps {
  children: React.ReactNode;
  onPress: () => void;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  accessibilityRole?: AccessibilityRole;
}

const GlowTouchable: React.FC<GlowTouchableProps> = ({
  children,
  onPress,
  style,
  accessibilityLabel,
  accessibilityRole = "button" as AccessibilityRole
}) => {
  const { colors } = useAppTheme();
  const glowAnim = useRef(new Animated.Value(0)).current;

  const handlePressIn = () => {
    Animated.timing(glowAnim, {
      toValue: 1,
      duration: 120,
      useNativeDriver: false
    }).start();
  };

  const handlePressOut = () => {
    Animated.timing(glowAnim, {
      toValue: 0,
      duration: 180,
      useNativeDriver: false
    }).start();
  };

  const animatedStyle = {
    opacity: glowAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 1] }), // Fixed opacity to always be visible
    transform: [{ scale: glowAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 1.05] }) }], // Fixed scale issue
    borderColor: glowAnim.interpolate({ inputRange: [0, 1], outputRange: [colors.outlineVariant, colors.accent] }),
    borderWidth: 1.5
  };

  return (
    <Animated.View style={[animatedStyle, style]}> 
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={onPress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        accessibilityRole={accessibilityRole}
        accessibilityLabel={accessibilityLabel}
      >
        {children}
      </TouchableOpacity>
    </Animated.View>
  );
};

export default GlowTouchable;
